<template>
	<div v-loading="loading" class="user-home-page" element-loading-text="正在加载主页...">
		<section class="identity-card">
			<div class="identity-main">
				<div class="identity-avatar">
					<img src="@/assets/user/user-avator.png" alt="用户头像" />
					<span><i :class="isEnterprise ? 'el-icon-office-building' : 'el-icon-user'"></i></span>
				</div>
				<div class="identity-copy">
					<div class="identity-title">
						<h1>{{ displayName }}</h1>
						<span>{{ isEnterprise ? '企业账号' : '个人账号' }}</span>
					</div>
					<p>{{ greetingText }}</p>
					<div class="identity-facts">
						<template v-if="isEnterprise">
							<div><small>统一社会信用代码</small><strong>{{ currentUserInfo.companyId || '暂未完善' }}</strong></div>
							<div><small>企业联系人</small><strong>{{ currentUserInfo.realName || '-' }}</strong></div>
							<div><small>联系电话</small><strong>{{ maskedPhone }}</strong></div>
						</template>
						<template v-else>
							<div><small>手机号</small><strong>{{ maskedPhone }}</strong></div>
							<div><small>邮箱</small><strong>{{ userEmail }}</strong></div>
							<div><small>企业绑定</small><strong>{{ currentUserInfo.companyName || '暂未绑定企业' }}</strong></div>
						</template>
					</div>
				</div>
			</div>
			<button type="button" class="text-button" @click="goTo('/user/profile')">
				查看个人资料 <i class="el-icon-arrow-right"></i>
			</button>
		</section>

		<div class="overview-grid">
			<section class="section-card order-card">
				<header class="section-heading">
					<div class="heading-icon heading-icon--order"><i class="el-icon-shopping-bag-2"></i></div>
					<div class="heading-copy"><h2>购物订单</h2><p>按订单状态快速查看</p></div>
					<button type="button" class="text-button" @click="goTo('/user/shoppmanag/shoppOrder')">
						全部订单 <i class="el-icon-arrow-right"></i>
					</button>
				</header>
				<div class="order-status-grid">
					<button
						v-for="item in orderStatusItems"
						:key="item.key"
						type="button"
						:class="['order-status-item', `is-${item.key}`]"
						@click="goToOrders(item.state)"
					>
						<span class="status-icon"><i :class="item.icon"></i></span>
						<span class="status-copy"><strong>{{ item.label }}</strong><small>{{ item.description }}</small></span>
						<span class="status-count"><b>{{ orderCounts[item.key] }}</b><small>笔</small></span>
						<i class="el-icon-arrow-right status-arrow"></i>
					</button>
				</div>
			</section>

			<div class="action-column">
				<button type="button" class="action-card cart-card" @click="goTo('/user/shoppmanag/shoppingCart')">
					<span class="action-icon"><i class="el-icon-shopping-cart-2"></i></span>
					<span class="action-copy"><strong>我的购物车</strong><small>查看已加入的商品</small></span>
					<span class="action-count"><b>{{ cartCount }}</b><small>件</small></span>
					<i class="el-icon-arrow-right action-arrow"></i>
				</button>
				<button type="button" class="action-card message-card" @click="goTo('/user/message')">
					<span class="action-icon"><i class="el-icon-bell"></i></span>
					<span class="action-copy"><strong>我的消息</strong><small>{{ messageDescription }}</small></span>
					<span class="action-count"><b>{{ messageCount }}</b><small>条未读</small></span>
					<i class="el-icon-arrow-right action-arrow"></i>
				</button>
			</div>
		</div>

		<section class="section-card coupon-section">
			<header class="section-heading coupon-heading">
				<div class="heading-icon heading-icon--coupon"><i class="el-icon-tickets"></i></div>
				<div class="heading-copy"><h2>我的券包</h2><p>共 {{ couponCount }} 张未使用优惠券</p></div>
				<button type="button" class="text-button" @click="goTo('/user/shoppmanag/myCoupon')">
					查看全部 <i class="el-icon-arrow-right"></i>
				</button>
			</header>
			<div v-if="coupons.length" class="coupon-grid">
				<wallet-coupon-card v-for="coupon in coupons" :key="coupon.id" :coupon="coupon" @use="goToMall" />
			</div>
			<div v-else class="coupon-empty">
				<span><i class="el-icon-tickets"></i></span>
				<div><strong>暂无可用优惠券</strong><p>前往商城看看近期服务与商品</p></div>
				<el-button type="primary" plain @click="goToMall">去逛逛</el-button>
			</div>
		</section>
	</div>
</template>

<script>
	import { mapGetters, mapState } from 'vuex'
	import { getUserInfo } from '@/api/userCenter'
	import { getMyCouponPage } from '@/api/coupon'
	import { getCountUserMessage } from '@/api/user/message'
	import { queryMerchantShoppingCartCount, queryMyPurchaseOrderPage } from '@/api/mall'
	import WalletCouponCard from './shoppmanag/components/WalletCouponCard'

	// 主页关注的订单状态配置，同时用于生成统计卡片和对应的查询参数。
	const ORDER_STATUS_ITEMS = [
		{ key: 'unpaid', label: '待支付', description: '待完成付款', state: 0, icon: 'el-icon-wallet' },
		{ key: 'paid', label: '已支付', description: '已完成付款', state: 1, icon: 'el-icon-circle-check' },
		{ key: 'pending', label: '待审核', description: '等待平台审核', state: 10, icon: 'el-icon-time' }
	]

	export default {
		name: 'UserHome',
		components: { WalletCouponCard },
		data() {
			return {
				// 首页各业务数据并行加载期间的统一遮罩状态。
				loading: false,
				// 首页单独请求到的最新用户资料，优先于 Vuex 中的缓存数据。
				homeUserInfo: {},
				// 购物车商品种类数量。
				cartCount: 0,
				// 当前用户未读消息数量。
				messageCount: 0,
				// 按待支付、已支付、待审核三个状态保存订单数量。
				orderCounts: { unpaid: 0, paid: 0, pending: 0 },
				// 未使用优惠券总数及首页展示列表。
				couponCount: 0,
				coupons: []
			}
		},
		computed: {
			...mapGetters(['userInfo', 'userType']),
			...mapState({ cachedUnreadMessageCount: state => state.user.userUnreadMessageCount }),
			orderStatusItems() {
				return ORDER_STATUS_ITEMS
			},
			// 接口数据加载成功后使用最新资料，否则回退到登录时缓存的用户资料。
			currentUserInfo() {
				return Object.keys(this.homeUserInfo).length ? this.homeUserInfo : this.userInfo || {}
			},
			// 用户类型字典：2 为企业账号，其他类型按个人账号展示。
			isEnterprise() {
				const infoType = this.currentUserInfo.sysUserVO && this.currentUserInfo.sysUserVO.userType
				return Number(infoType || this.userType) === 2
			},
			displayName() {
				if (this.isEnterprise) return this.currentUserInfo.companyName || this.currentUserInfo.realName || '企业用户'
				return this.currentUserInfo.realName || '楚建通用户'
			},
			greetingText() {
				if (this.isEnterprise) return '欢迎回来，企业服务与采购信息都在这里'
				const days = this.currentUserInfo.registerTime
				return days || days === 0 ? `楚建通已陪伴您 ${days} 天` : '欢迎回来，今天也为您准备好了便捷服务'
			},
			// 首页只展示脱敏手机号，避免直接暴露完整联系方式。
			maskedPhone() {
				const phone = String(this.currentUserInfo.phone || '')
				return /^\d{11}$/.test(phone) ? phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : phone || '暂未完善'
			},
			userEmail() {
				return (this.currentUserInfo.sysUserVO && this.currentUserInfo.sysUserVO.email) || this.currentUserInfo.email || '暂未绑定'
			},
			messageDescription() {
				return this.messageCount > 0 ? '有新消息等待查看' : '暂无新的未读消息'
			}
		},
		created() {
			// 先使用顶部导航已获取的消息数，接口返回后再刷新，减少数字闪烁。
			this.messageCount = Number(this.cachedUnreadMessageCount || 0)
			this.loadHomeData()
		},
		methods: {
			// 并行加载用户、购物车、三类订单、未读消息和未使用优惠券数据。
			async loadHomeData() {
				this.loading = true
				// 单个板块请求失败时返回空数据，避免影响其他板块正常展示。
				const safeRequest = request => request.catch(() => ({ data: null }))
				try {
					const requests = [
						// 用户信息板块：获取姓名、账号类型、手机号、邮箱及绑定企业等最新资料。
						safeRequest(getUserInfo()),
						// 购物车板块：查询当前用户购物车中的商品种类数量。
						safeRequest(queryMerchantShoppingCartCount()),
						// 购物订单板块：分别按待支付、已支付、待审核状态查询分页总数；每类只取一条以减少返回数据量。
						...ORDER_STATUS_ITEMS.map(item =>
							safeRequest(queryMyPurchaseOrderPage({ page: 1, size: 1, orderState: item.state }))
						),
						// 我的消息板块：msgConsumeState=0 表示仅统计未读消息。
						safeRequest(getCountUserMessage({ msgType: '1', msgConsumeState: '0' })),
						// 我的券包板块：status=10 表示未使用，首页最多展示两张券。
						safeRequest(getMyCouponPage({ page: 1, size: 2, status: 10 }))
					]
					const [userResponse, cartResponse, ...resultResponses] = await Promise.all(requests)
					this.applyUserData(userResponse.data)
					this.cartCount = this.getCountValue(cartResponse.data)
					// resultResponses 前三项与 ORDER_STATUS_ITEMS 顺序一致。
					ORDER_STATUS_ITEMS.forEach((item, index) => {
						this.orderCounts[item.key] = this.getTotalValue(resultResponses[index].data)
					})
					const messageResponse = resultResponses[ORDER_STATUS_ITEMS.length]
					const couponResponse = resultResponses[ORDER_STATUS_ITEMS.length + 1]
					if (messageResponse.data !== null) {
						this.messageCount = this.getCountValue(messageResponse.data)
						this.$store.dispatch('user/setUnreadMessageCount', this.messageCount)
					}
					this.applyCouponData(couponResponse.data)
				} finally {
					this.loading = false
				}
			},
			// 仅接受有效对象作为用户资料，防止异常响应覆盖 Vuex 回退数据。
			applyUserData(data) {
				if (data && typeof data === 'object') this.homeUserInfo = data
			},
			// 兼容数量接口直接返回数字或返回 count/total/quantity 对象的情况。
			getCountValue(data) {
				if (typeof data === 'number') return data
				if (!data || typeof data !== 'object') return 0
				return Number(data.count || data.total || data.quantity || 0)
			},
			// 从分页接口结果中提取总条数，用于订单状态统计。
			getTotalValue(data) {
				return Number((data && (data.total || data.count)) || 0)
			},
			// 首页仅保留前两张未使用优惠券，完整列表在“我的券包”中查看。
			applyCouponData(data) {
				const records = (data && (data.records || data.list)) || []
				this.couponCount = Number((data && data.total) || records.length || 0)
				this.coupons = records.slice(0, 2).map(coupon => ({ ...coupon, couponStatus: 'unused' }))
			},
			goTo(path) {
				this.$router.push(path)
			},
			// 携带订单状态进入订单列表，使目标页面自动应用对应筛选条件。
			goToOrders(orderState) {
				this.$router.push({ path: '/user/shoppmanag/shoppOrder', query: { orderState: String(orderState) } })
			},
			goToMall() {
				this.$router.push('/mallStore')
			}
		}
	}
</script>

<style lang="scss" scoped>
	.user-home-page {
		box-sizing: border-box;
		height: 100%;
		padding: 22px;
		overflow-x: hidden;
		overflow-y: auto;
		color: #26384a;
		font-size: 14px;
		background: #fff;
	}
	.identity-card {
		position: relative;
		display: flex;
		min-height: 166px;
		padding: 27px 30px;
		align-items: center;
		justify-content: space-between;
		overflow: hidden;
		border: 1px solid #dce9f5;
		border-radius: 8px;
		background: linear-gradient(110deg, #f2f8fe 0%, #f8fbff 55%, #eef6fd 100%);
	}
	.identity-card::after {
		content: '';
		position: absolute;
		right: -65px;
		bottom: -130px;
		width: 290px;
		height: 290px;
		border: 42px solid rgba(41, 120, 198, 0.045);
		border-radius: 50%;
	}
	.identity-main { position: relative; z-index: 1; display: flex; min-width: 0; align-items: center; }
	.identity-avatar { position: relative; flex: 0 0 82px; width: 82px; height: 82px; margin-right: 22px; padding: 4px; border-radius: 50%; background: #dcecfb; }
	.identity-avatar img { display: block; width: 74px; height: 74px; border-radius: 50%; object-fit: cover; }
	.identity-avatar span { position: absolute; right: -2px; bottom: 1px; display: flex; width: 27px; height: 27px; align-items: center; justify-content: center; border: 2px solid #e5f1fb; border-radius: 50%; color: #fff; background: #2675c3; font-size: 13px; }
	.identity-copy { min-width: 0; }
	.identity-title { display: flex; align-items: center; }
	.identity-title h1 { max-width: 440px; margin: 0 12px 0 0; overflow: hidden; color: #24384c; font-size: 23px; line-height: 31px; text-overflow: ellipsis; white-space: nowrap; }
	.identity-title span { padding: 4px 10px; border: 1px solid #bad4ec; border-radius: 12px; color: #3174b6; background: #e8f3fd; font-size: 12px; }
	.identity-copy > p { margin: 5px 0 18px; color: #7c8fa2; }
	.identity-facts { display: flex; }
	.identity-facts > div { min-width: 128px; max-width: 235px; padding-right: 26px; }
	.identity-facts > div + div { padding-left: 26px; border-left: 1px solid #d6e2ed; }
	.identity-facts small, .identity-facts strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.identity-facts small { margin-bottom: 5px; color: #94a1ae; font-size: 12px; font-weight: 400; }
	.identity-facts strong { color: #3a4e62; font-size: 14px; font-weight: 500; }
	.text-button { position: relative; z-index: 1; padding: 7px 0; border: 0; color: #3177b9; background: transparent; font-size: 14px; cursor: pointer; }
	.text-button:hover, .text-button:focus-visible { outline: none; color: #155fa6; }
	.text-button i { margin-left: 4px; }
	.overview-grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(285px, 0.85fr); gap: 18px; margin-top: 18px; }
	.section-card { border: 1px solid #e1e8ef; border-radius: 8px; background: #fff; }
	.order-card { padding: 21px 22px 22px; }
	.section-heading { display: flex; align-items: center; }
	.heading-icon { display: flex; flex: 0 0 42px; width: 42px; height: 42px; margin-right: 12px; align-items: center; justify-content: center; border-radius: 12px; font-size: 21px; }
	.heading-icon--order { color: #337dc4; background: #eaf4fd; }
	.heading-icon--coupon { color: #d37627; background: #fff2e5; }
	.heading-copy { flex: 1; min-width: 0; }
	.heading-copy h2 { margin: 0; color: #293d50; font-size: 18px; line-height: 24px; }
	.heading-copy p { margin: 3px 0 0; color: #929fac; font-size: 13px; }
	.order-status-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 21px; }
	.order-status-item { position: relative; display: grid; min-height: 116px; padding: 17px 14px; align-items: center; grid-template-columns: 38px minmax(0, 1fr); column-gap: 10px; overflow: hidden; border: 1px solid #e1e9f1; border-radius: 7px; color: #33475b; text-align: left; background: #f8fbfe; cursor: pointer; transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease; }
	.order-status-item:hover, .order-status-item:focus-visible { outline: none; border-color: #9cc3e5; box-shadow: 0 7px 15px rgba(50, 105, 158, 0.09); transform: translateY(-2px); }
	.status-icon { display: flex; width: 38px; height: 38px; align-items: center; justify-content: center; border-radius: 50%; color: #dc822e; background: #fff0df; font-size: 18px; }
	.is-paid .status-icon { color: #3a9a72; background: #e5f6ef; }
	.is-pending .status-icon { color: #667dcc; background: #e9edfc; }
	.status-copy strong, .status-copy small { display: block; }
	.status-copy strong { margin-bottom: 4px; font-size: 15px; }
	.status-copy small { color: #98a3ae; font-size: 12px; }
	.status-count { grid-column: 1 / -1; padding-left: 48px; color: #2d72b6; white-space: nowrap; }
	.status-count b { font-size: 27px; font-weight: 600; }
	.status-count small { margin-left: 4px; color: #8794a1; font-size: 12px; }
	.status-arrow { position: absolute; right: 13px; bottom: 18px; color: #afbbc6; }
	.action-column { display: grid; grid-template-rows: repeat(2, minmax(0, 1fr)); gap: 14px; }
	.action-card { position: relative; display: grid; min-height: 101px; padding: 18px 18px; align-items: center; grid-template-columns: 50px minmax(0, 1fr) auto; column-gap: 13px; overflow: hidden; border: 1px solid #dce8f3; border-radius: 8px; color: #2c4053; text-align: left; background: #f3f8fd; cursor: pointer; transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease; }
	.action-card:hover, .action-card:focus-visible { outline: none; border-color: #94bee1; box-shadow: 0 7px 16px rgba(50, 105, 158, 0.09); transform: translateY(-2px); }
	.action-card::after { content: ''; position: absolute; right: -45px; bottom: -56px; width: 130px; height: 130px; border: 18px solid rgba(45, 119, 185, 0.045); border-radius: 50%; }
	.message-card { border-color: #e7e2f1; background: #f8f6fc; }
	.action-icon { display: flex; width: 50px; height: 50px; align-items: center; justify-content: center; border-radius: 13px; color: #fff; background: linear-gradient(145deg, #4c9adf, #2674bd); box-shadow: 0 6px 13px rgba(38, 116, 189, 0.17); font-size: 23px; }
	.message-card .action-icon { background: linear-gradient(145deg, #8b83cf, #675db4); box-shadow: 0 6px 13px rgba(103, 93, 180, 0.17); }
	.action-copy strong, .action-copy small { display: block; }
	.action-copy strong { margin-bottom: 6px; font-size: 16px; }
	.action-copy small { overflow: hidden; color: #8794a1; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
	.action-count { position: relative; z-index: 1; padding-right: 16px; color: #2c75ba; text-align: right; white-space: nowrap; }
	.message-card .action-count { color: #6b62b6; }
	.action-count b { display: block; font-size: 27px; line-height: 30px; }
	.action-count small { color: #8794a1; font-size: 12px; }
	.action-arrow { position: absolute; right: 10px; top: 50%; color: #b0bcc7; transform: translateY(-50%); }
	.coupon-section { margin-top: 18px; padding: 21px 22px 24px; }
	.coupon-heading { padding-bottom: 17px; border-bottom: 1px solid #e7edf2; }
	.coupon-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; margin-top: 20px; }
	.coupon-empty { display: flex; min-height: 116px; margin-top: 20px; padding: 16px 24px; align-items: center; border: 1px dashed #d4e0ea; border-radius: 7px; background: #fafcfe; }
	.coupon-empty > span { display: flex; width: 54px; height: 54px; margin-right: 17px; align-items: center; justify-content: center; border-radius: 50%; color: #6e9dca; background: #eaf3fb; font-size: 25px; }
	.coupon-empty > div { flex: 1; }
	.coupon-empty strong { font-size: 16px; }
	.coupon-empty p { margin: 6px 0 0; color: #8d98a5; font-size: 13px; }
	@media (max-width: 1280px) {
		.overview-grid { grid-template-columns: 1fr; }
		.action-column { grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: none; }
		.identity-facts > div { min-width: 110px; padding-right: 17px; }
		.identity-facts > div + div { padding-left: 17px; }
	}
	@media (max-width: 1000px) {
		.identity-card { display: block; }
		.identity-card > .text-button { margin: 18px 0 0 104px; }
		.order-status-grid, .action-column, .coupon-grid { grid-template-columns: 1fr; }
	}
	@media (prefers-reduced-motion: reduce) { .order-status-item, .action-card { transition: none; } }
</style>
